import { approve } from '@arcxgame/contracts'
import {
  BaseERC20Factory,
  KermanERC20Factory,
  KermanRewardsFactory,
} from '@arcxgame/contracts/dist/src/typings'
import { useWeb3React } from '@web3-react/core'
import { BigNumber } from 'ethers'
import { useAsync } from 'hooks'
import { useState } from 'react'
import { loadContract, waitTxn } from 'utils'

export const useStaking = () => {
  const { chainId, library, account } = useWeb3React()
  const [loadingApprove, setLoadingApprove] = useState(false)
  const [loadingStake, setLoadingStake] = useState(false)
  const [loadingClaim, setLoadingClaim] = useState(false)

  const { address: kermanRewardsAddress } = loadContract({
    name: 'KermanRewardsProxy',
    network: chainId?.toString() || '1',
  })

  const signer = library && library.getSigner()

  const kermanRewardsContract = KermanRewardsFactory.connect(kermanRewardsAddress, signer)

  const [stakingDetails, , loadingDetails, refresh] = useAsync(async () => {
    if (!account || !library) return null

    try {
      // $KERMAN
      const stakingTokenAddress = await kermanRewardsContract.stakingToken()
      const stakingTokenContract = KermanERC20Factory.connect(stakingTokenAddress, signer)
      const stakingTokenSymbol = await stakingTokenContract.symbol()
      const stakingTokenDecimals = await stakingTokenContract.decimals()
      const stakingTokenBalance = await stakingTokenContract.balanceOf(account)
      const stakingTokenStakedBalance = await kermanRewardsContract.staked(account)
      const stakingTokenAllowance = await stakingTokenContract.allowance(
        account,
        kermanRewardsAddress,
      )
      const needsApproval = stakingTokenAllowance.lt(stakingTokenBalance)

      // $ARCX
      const rewardsTokenAddress = await kermanRewardsContract.rewardsToken()
      const rewardsTokenContract = BaseERC20Factory.connect(rewardsTokenAddress, signer)
      const rewardsTokenSymbol = await rewardsTokenContract.symbol()
      const rewardsTokenClaimedBalance = await kermanRewardsContract.claimed(account)
      const rewardsTokenRewardsAvailable = await kermanRewardsContract.rewardsAvailable(account)
      const rewardsTokenStopTime = await kermanRewardsContract.sablierStopTime()

      // Stake Deadline
      const stakeDeadline = await kermanRewardsContract.stakeDeadline()

      return {
        stakingTokenAddress,
        stakingTokenSymbol,
        stakingTokenDecimals,
        stakingTokenBalance,
        stakingTokenStakedBalance,
        needsApproval,
        rewardsTokenSymbol,
        rewardsTokenClaimedBalance,
        stakeDeadline,
        rewardsTokenRewardsAvailable,
        rewardsTokenStopTime,
      }
    } catch (error) {
      console.log(error)
      return null
    }
  }, [chainId, account])

  const claim = async () => {
    setLoadingClaim(true)
    try {
      await waitTxn(kermanRewardsContract.claim)
      console.log('refresh')
      await refresh()
    } catch (error) {
      console.error('ERROR: claim', error)
    }
    setLoadingClaim(false)
  }

  const stake = async () => {
    setLoadingStake(true)
    try {
      const { wait } = await kermanRewardsContract.connect(signer).stake()
      await wait()
      await refresh()
    } catch (error) {
      console.error('ERROR: stake', error)
    }
    setLoadingStake(false)
  }

  const approveStakingToken = async () => {
    const { stakingTokenAddress, stakingTokenBalance } = stakingDetails
    setLoadingApprove(true)
    try {
      await approve(stakingTokenBalance, stakingTokenAddress, kermanRewardsAddress, signer)
      await refresh()
    } catch (error) {
      console.error('ERROR: approve', error)
    }
    setLoadingApprove(false)
  }

  return {
    stakingTokenAddress: stakingDetails?.stakingTokenAddress || '',
    stakingTokenSymbol: stakingDetails?.stakingTokenSymbol || 'KERMAN',
    stakingTokenDecimals: stakingDetails?.stakingTokenDecimals,
    stakingTokenBalance: stakingDetails?.stakingTokenBalance || BigNumber.from(0),
    stakingTokenStakedBalance: stakingDetails?.stakingTokenStakedBalance || BigNumber.from(0),
    rewardsTokenSymbol: stakingDetails?.rewardsTokenSymbol || 'ARCX',
    needsApproval: stakingDetails?.needsApproval,
    rewardsTokenClaimedBalance: stakingDetails?.rewardsTokenClaimedBalance || BigNumber.from(0),
    rewardsTokenRewardsAvailable: stakingDetails?.rewardsTokenRewardsAvailable || BigNumber.from(0),
    stakeDeadline: stakingDetails?.stakeDeadline || BigNumber.from(0),
    rewardsTokenStopTime: stakingDetails?.rewardsTokenStopTime || BigNumber.from(0),
    loadingApprove,
    loadingStake,
    loadingClaim,
    loadingDetails,
    claim,
    stake,
    approveStakingToken,
  }
}
